import React, { useEffect, useMemo, useState } from "react";
import { getAudioPeaks } from "../../utils/audioPeaks";
import "./Waveform.scss";

// Placeholder pattern shown while real peaks are loading, or when they can't
// be decoded (missing/CORS-blocked source) — same look as before this change.
function getFallbackHeights(count) {
  return Array.from(
    { length: count },
    (_, i) => 20 + Math.abs(Math.sin(i * 0.5) * 30 + Math.sin(i * 1.7) * 15 + Math.sin(i * 3.1) * 8)
  );
}

// Resamples the [trimStartSec, trimEndSec) slice of the decoded peaks down
// to `barCount` bars, so a clip only shows the waveform for the seconds of
// the source file it actually plays — the piece that matters for telling
// split siblings apart.
function sliceToBars(peaksData, trimStartSec, trimEndSec, barCount) {
  const { peaks, duration } = peaksData;
  const total = peaks.length;
  if (!total || !duration) return null;

  const startRatio = Math.max(0, Math.min(1, trimStartSec / duration));
  const endRatio = Math.max(startRatio, Math.min(1, trimEndSec / duration));
  const startIdx = Math.floor(startRatio * total);
  const endIdx = Math.max(startIdx + 1, Math.ceil(endRatio * total));
  const slice = peaks.slice(startIdx, endIdx);
  if (slice.length === 0) return null;

  const heights = new Array(barCount);
  for (let i = 0; i < barCount; i++) {
    const idx = Math.min(slice.length - 1, Math.floor((i / barCount) * slice.length));
    heights[i] = 8 + slice[idx] * 92; // 8%..100% so silent stretches stay visible
  }
  return heights;
}

function Waveform({ clipWidth, url, trimStartSec = 0, trimEndSec = null }) {
  const [peaksData, setPeaksData] = useState(null);
  const barCount = Math.max(0, Math.floor(clipWidth / 4));

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    getAudioPeaks(url).then((result) => {
      if (!cancelled) setPeaksData(result);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const waveHeights = useMemo(() => {
    if (peaksData && typeof trimEndSec === "number") {
      const sliced = sliceToBars(peaksData, trimStartSec, trimEndSec, barCount);
      if (sliced) return sliced;
    }
    return getFallbackHeights(barCount);
  }, [peaksData, trimStartSec, trimEndSec, barCount]);

  return (
    <div className="it-waveform">
      {waveHeights.map((h, i) => (
        <div key={i} className="it-waveform-bar" style={{ height: `${h}%` }} />
      ))}
    </div>
  );
}

export default Waveform;
